/**
 * Manages which timeline is active when multiple timelines are available.
 *
 * Active timeline state is owned by the caller via `activeIndex` / `onActiveChange`
 * props, so both apps can persist it however they like (URL params, Zustand, etc.).
 */

import { useCallback, useMemo } from "react";

import type { Timeline } from "../core";

// =============================================================================
// Types
// =============================================================================

export interface UseActiveTimelineResult {
  /** The currently active Timeline, or `undefined` when `timelines` is empty. */
  active: Timeline | undefined;
  /** 0-based index of the active timeline. */
  activeIndex: number;
  /** Switch to a different timeline by index. Resets selection. */
  setActive: (index: number) => void;
  /** All available timelines. */
  timelines: Timeline[];
}

export interface UseActiveTimelineProps {
  /** Currently active timeline index (externally managed). */
  activeIndex: number;
  /** Called when the user switches timelines. */
  onActiveChange: (index: number) => void;
}

// =============================================================================
// Hook
// =============================================================================

export function useActiveTimeline(
  timelines: Timeline[],
  props?: UseActiveTimelineProps
): UseActiveTimelineResult {
  const propsActiveIndex = props?.activeIndex;
  const onActiveChange = props?.onActiveChange;

  const activeIndex = useMemo(() => {
    const idx = propsActiveIndex ?? 0;
    return idx >= 0 && idx < timelines.length ? idx : 0;
  }, [propsActiveIndex, timelines.length]);

  const active = timelines[activeIndex] ?? timelines[0];

  const setActive = useCallback(
    (index: number) => {
      if (index < 0 || index >= timelines.length) return;
      onActiveChange?.(index);
    },
    [onActiveChange, timelines.length]
  );

  return { active, activeIndex, setActive, timelines };
}
